import { useRouter } from '@tanstack/react-router'
import { FileText } from '@/components/icons'
import { AppLayout } from './AppLayout'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { EmptyState } from './EmptyState'
import { DetailSkeleton } from './ui/skeleton-presets'
import { useDocumentGetById } from '@/lib/hooks/document'
import { ViewDocumentDrawer } from './documents/ViewDocumentDrawer'

interface DocumentDeepLinkViewProps {
  documentId: string
}

/**
 * Standalone page for a document deep link (e.g. from a notification
 * `/documents/{id}`). Fetches the document and opens the view drawer that
 * matches its category, since documents have no shared detail page.
 */
export function DocumentDeepLinkView({ documentId }: DocumentDeepLinkViewProps) {
  const router = useRouter()
  const { data: doc, isLoading, isError } = useDocumentGetById(documentId)

  // Return to wherever the user came from (usually the inbox); fall back to the
  // inbox when there's no history (e.g. cold start from a push notification).
  const close = () => {
    if (router.history.canGoBack()) {
      router.history.back()
    } else {
      router.navigate({ to: '/inbox' })
    }
  }

  return (
    <AppLayout
      topBar={<TopBarBackIconButton title="Document" showBackButton />}
      className="pt-4 flex flex-col gap-4"
    >
      {isLoading ? (
        <DetailSkeleton />
      ) : isError || !doc ? (
        <EmptyState
          icon={FileText}
          title="Document not found"
          description="This document may have been removed or you don't have access to it."
        />
      ) : null}

      {doc && (
        <ViewDocumentDrawer
          isOpen
          onClose={close}
          documentId={documentId}
        />
      )}
    </AppLayout>
  )
}
